import React, { useContext, useEffect, useState } from "react";
import styles from "./Post.module.css";
import ShareComponent from "./Share/ShareComponent";
import { PostContext } from "../../createContext/Post/PostContext";
import { MessageCircle, Heart, PenSquare } from "lucide-react";
import IsAuth from "../../createContext/is-Auth/IsAuthContext";
import { Link } from "react-router-dom";
import CommentSection from "./Commet/Comment";

function Post({
  _id,
  creator,
  createdAt,
  mediaType,
  comments,
  content,
  mediaURL,
  likes,
  toggleComments,
  isCommentsVisible
}) {
  const { user } = useContext(IsAuth);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { toggleLike, editPost } = useContext(PostContext);
  const [isLiked, setLiked] = useState(false);
  const [currLikes, setCurrLikes] = useState(likes);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(content);

  const isCreator = user && creator && user._id === creator._id;

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  useEffect(() => {
    if (user && user.likes.includes(_id)) {
      setLiked(true);
    }
  }, [user]);

  const handleLike = async () => {
    setLiked(!isLiked);
    const newLikes = isLiked ? currLikes - 1 : currLikes + 1;
    setCurrLikes(newLikes);
    await toggleLike(_id);
  };

  const handleEdit = async () => {
    if (isEditing) {
      try {
        if (editedContent.trim() !== content){
          await editPost(_id, editedContent);
        }
        setIsEditing(false);
      } catch (error) {
        console.error("Error editing post:", error);
      }
    } else {
      setIsEditing(true);
    }
  };

  const numberFilter = (num) => {
    const lookup = [
      { value: 1, symbol: "" },
      { value: 1e3, symbol: "k" },
      { value: 1e6, symbol: "M" },
      { value: 1e9, symbol: "G" },
      { value: 1e12, symbol: "T" },
      { value: 1e15, symbol: "P" },
      { value: 1e18, symbol: "E" },
    ];
    const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
    const item = lookup
      .slice()
      .reverse()
      .find((item) => num >= item.value);
    return item
      ? (num / item.value).toFixed(1).replace(rx, "$1") + item.symbol
      : "0";
  };

  return (
    <>
      <article className={styles.post}>
        <header className={styles.postHeader}>
          <img
            src={creator ? creator.avatar : ""}
            alt={`${creator ? creator.userName : " "}'s avatar`}
            className={styles.authorAvatar}
          />
          <div className={styles.authorInfo}>
            <Link to={`/profile/${creator.userName}`}>
              <h3 className={styles.authorName}>
                {creator ? creator.userName : ""}
              </h3>
            </Link>
            <time className={styles.postTime}>
              {new Date(createdAt)?.toLocaleString(undefined, {
                hour: "2-digit",
                minute: "2-digit",
                weekday: "short",
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </time>
          </div>
          {isCreator && (
            isEditing ? (
              <button
                onClick={handleEdit}
                className="px-3 py-1 text-sm rounded-lg hover:bg-gray-200 flex items-center gap-2 bg-gray-300"
              >
                <PenSquare size={20} />
                <span>Save Edit</span>
              </button>
            ) : (
              <button
                onClick={handleEdit}
                className={styles.postOptions}
                aria-label="Edit post"
              >
                <PenSquare size={20} />
              </button>
            )
          )}
        </header>
        
        {isEditing ? (
          <textarea
            value={editedContent}
            onChange={(e) => setEditedContent(e.target.value)}
            className={styles.postContent}
            style={{ width: '100%', minHeight: '100px', padding: '8px', marginBottom: '15px' }}
            autoFocus
          />
        ) : (
          <p className={styles.postContent}>{content}</p>
        )}

        {mediaURL &&
          (mediaType === "image" ? (
            <img src={mediaURL} alt="Post" className={styles.postImage} />
          ) : (
            mediaType === "video" && (
              <video src={mediaURL} className={styles.postImage} controls />
            )
          ))}
        <footer className={styles.postFooter}>
          <div className={styles.postActions}>
            <button
              aria-label="Like"
              className={styles.actionButton}
              onClick={handleLike}
            >
              {isLiked ? (
                <Heart
                  fill="#FA2A55"
                  color="#FA2A55"
                  strokeWidth={2.5}
                  size={24}
                />
              ) : (
                <Heart />
              )}
              <span className={styles.actionCount}>
                {numberFilter(currLikes)}
              </span>
            </button>
            <button
              aria-label="Comment"
              className={styles.actionButton}
              onClick={() => toggleComments(_id)}
            >
              <MessageCircle strokeWidth={2.5} size={24} />
              <span className={styles.actionCount}>
                {numberFilter(comments.length)}
              </span>
            </button>
            <button
              aria-label="Share"
              onClick={openModal}
              className={styles.actionButton}
            >
              <svg
                width={25}
                xmlns="http://www.w3.org/2000/svg"
                data-name="Layer 1"
                viewBox="0 0 24 24"
                id="share"
              >
                <path
                  fill="#000000"
                  d="m21.707 11.293-8-8A1 1 0 0 0 12 4v3.545A11.015 11.015 0 0 0 2 18.5V20a1 1 0 0 0 1.784.62 11.456 11.456 0 0 1 7.887-4.049c.05-.006.175-.016.329-.026V20a1 1 0 0 0 1.707.707l8-8a1 1 0 0 0 0-1.414ZM14 17.586V15.5a1 1 0 0 0-1-1c-.255 0-1.296.05-1.562.085a14.005 14.005 0 0 0-7.386 2.948A9.013 9.013 0 0 1 13 9.5a1 1 0 0 0 1-1V6.414L19.586 12Z"
                ></path>
              </svg>
            </button>
          </div>
        </footer>
      </article>
      
      {isCommentsVisible && (
        <div className={styles.inlineComments}>
          <CommentSection 
            postid={_id} 
            isInline={true} 
            onClose={() => toggleComments(_id)}
          />
        </div>
      )}
      
      <ShareComponent
        isOpen={isModalOpen}
        onClose={closeModal}
        postData={{
          _id,
          content,
          mediaURL,
          mediaType,
          creator,
        }}
      />
    </>
  );
}

export default Post;